/* AppraisalComponents/MultidekClipsBar.jsx
 * Phase C · C4e Sheet-adjacent extraction (2026-02c).
 * Extracted verbatim from AppraisalComponentsTab.js:4641-4773.
 * Sheet section bar for Multidek Clips & Bracket Screws.
 */
import React, { useState, useEffect } from 'react';

export function MultidekClipsBar({ lines, rates, onCommit }) {
  // Detect Multidek sheet lines (case-insensitive profile match,
  // ignore the accessory auto-lines themselves).
  const multidekLines = (lines || []).filter(l => {
    const profile = String(l.profile || '').toLowerCase();
    if (!profile.includes('multidek')) return false;
    if (/^\[AUTO:multidek_/i.test(l.notes || '')) return false;
    return true;
  });

  // Sum m² across all Multidek lines. Each Multidek rate has a 500mm
  // cover by default — pull the actual cover from the rate row if we
  // can find a match (catalogue may have varied covers later).
  const multidekRate = (rates || []).find(r =>
    /multidek/i.test(r.sheet_profile || r.type_name || '')
    && !r.is_accessory && (r.cover_width_mm)
  );
  const coverM = (Number(multidekRate?.cover_width_mm) || 500) / 1000;
  const totalM2 = multidekLines.reduce((acc, l) => {
    const q = Number(l.quantity) || 0;
    const len = Number(l.length) || 0;
    return acc + (q * len * coverM);
  }, 0);

  // Pull current saved state from the auto-lines.
  const clipsLine  = (lines || []).find(l => /^\[AUTO:multidek_clips\]/i.test(l.notes || ''));
  const screwsLine = (lines || []).find(l => /^\[AUTO:multidek_screws\]/i.test(l.notes || ''));
  const savedClips  = Number(clipsLine?.quantity) || 0;
  const savedScrews = Number(screwsLine?.quantity) || 0;
  // Buffer persists in the notes tag e.g. "buf=12%" — fall back to 10.
  const bufMatch = (clipsLine?.notes || '').match(/buf=(\d+)/);
  const savedBuf = bufMatch ? Number(bufMatch[1]) : 10;

  // IMPORTANT: All hooks must be called above any conditional return
  // so React keeps the call order stable across renders.
  const [buf, setBuf]       = React.useState(String(savedBuf));
  const [clips, setClips]   = React.useState(savedClips ? String(savedClips) : '');
  const [screws, setScrews] = React.useState(savedScrews ? String(savedScrews) : '');
  React.useEffect(() => { setBuf(String(savedBuf)); }, [savedBuf]);
  React.useEffect(() => { setClips(savedClips ? String(savedClips) : ''); }, [savedClips]);
  React.useEffect(() => { setScrews(savedScrews ? String(savedScrews) : ''); }, [savedScrews]);

  if (multidekLines.length === 0) return null;

  const derivedClips  = Math.ceil(totalM2 * 6 * (1 + (Number(buf) || 0) / 100));
  const derivedScrews = Math.ceil((Number(clips) || derivedClips) * 3 * (1 + (Number(buf) || 0) / 100));

  const recomputeBoth = () => {
    const c = derivedClips;
    const s = Math.ceil(c * 3 * (1 + (Number(buf) || 0) / 100));
    setClips(String(c));
    setScrews(String(s));
    onCommit({ clips_qty: c, screws_qty: s, buf_pct: Number(buf) || 0 });
  };
  const commit = () => {
    const c = clips === '' ? derivedClips : (Math.max(0, Math.floor(Number(clips) || 0)));
    const s = screws === '' ? Math.ceil(c * 3 * (1 + (Number(buf) || 0) / 100))
                            : (Math.max(0, Math.floor(Number(screws) || 0)));
    onCommit({ clips_qty: c, screws_qty: s, buf_pct: Number(buf) || 0 });
  };

  return (
    <div
      data-testid="multidek-clips-bar"
      className="rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-2 space-y-2"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-700">
          🔩 Multidek Accessories
        </span>
        <span className="text-[10px] font-mono-num text-slate-600">
          Multidek area: <strong className="text-slate-800">{totalM2.toFixed(2)} m²</strong>
        </span>
        <span className="text-[10px] text-slate-500">·  6 clips/m² · 3 screws/clip</span>
        <button
          type="button"
          onClick={recomputeBoth}
          data-testid="multidek-recompute"
          className="ml-auto text-[10px] px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 font-semibold"
          title="Reset to auto-derived values"
        >↻ Recompute</button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Buffer %</span>
          <input
            type="number" min={0} max={100} step={1}
            value={buf}
            onChange={(e) => setBuf(e.target.value)}
            onBlur={commit}
            data-testid="multidek-buffer-input"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Clips qty <span className="text-slate-400 normal-case">(auto {derivedClips})</span></span>
          <input
            type="number" min={0} step={1}
            value={clips}
            onChange={(e) => setClips(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            placeholder={String(derivedClips)}
            data-testid="multidek-clips-input"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Bracket screws qty <span className="text-slate-400 normal-case">(auto {derivedScrews})</span></span>
          <input
            type="number" min={0} step={1}
            value={screws}
            onChange={(e) => setScrews(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            placeholder={String(derivedScrews)}
            data-testid="multidek-screws-input"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white"
          />
        </label>
      </div>
      {(savedClips || savedScrews) ? (
        <div className="text-[10px] text-emerald-700">
          live · clips × {savedClips} · screws × {savedScrews}
        </div>
      ) : (
        <div className="text-[10px] text-amber-700">
          Not saved yet — click anywhere outside or hit ↻ Recompute to commit.
        </div>
      )}
    </div>
  );
}
